import { useMaintenanceCheck, MaintenanceScreen } from '../MaintenanceGate';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, regionsOf, regionLabel } from '../AuthContext';

// -- Language --------------------------------------------------
const LANG = {
  en: {
    title: 'Credit Application System',
    signOut: 'Sign out',
    dashboard: 'My Submissions',

    step1Title: 'Enter Outlet ID',
    outletIdPlaceholder: 'e.g. NM2004676',
    checkBtn: 'Search',
    checking: 'Searching...',
    outletNotFound: 'Outlet ID not found. This could mean the ID was entered incorrectly, or the outlet was recently created and has not yet been synced to our system. Data is updated daily - please verify the ID or try again tomorrow.',
    outletId: 'Outlet ID',
    outletName: 'Outlet Name',
    siteId: 'Site ID',
    vanId: 'Van ID',
    creditLimit: 'Credit Limit',
    paymentTerm: 'Payment Terms',
    regNo: 'Registration Number',
    confirmPrompt: 'Please confirm the outlet details above are correct before proceeding.',
    confirmYes: 'Yes, Proceed',
    confirmNo: 'No, Re-enter',
    checkingRisk: 'Please wait, outlet records are being checked...',

    step2Title: 'Risk Assessment',
    blacklistCard: 'Blacklist Status',
    blacklistNil: 'Nil',
    blacklistHit: 'Blacklisted',
    arCard: 'Current AR Status',
    currentAR: 'Current AR',
    totalOverdue: 'Total Overdue',
    latePayCard: 'Late Payment History',
    latePayNote: 'Late payment records detected. Please provide remarks or justification at a later stage.',
    invoiceId: 'Invoice ID',
    invoiceDate: 'Invoice Date',
    dueDate: 'Due Date',
    amount: 'Amount (RM)',
    dpd: 'DPD',
    dpdLabel: 'DPD',
    lastLimitCard: 'Credit History',
    noLastLimit: 'No prior credit decision found.',
    finalLimit: 'Final Limit',
    effectiveDate: 'Effective Date',
    decision: 'Decision',
    remark: 'Remark',
    kycStatus: 'KYC Status',
    approver: 'Approver',
    source: 'Source',
    blacklistBlock: 'This outlet has been flagged in our blacklist. Submission is not permitted. Please contact your credit manager for clarification.',
    overdueBlock: 'This outlet has outstanding overdue invoices totalling RM{amount}. Please ensure full collection before resubmitting.',
    dataDisclaimer: 'Note: Payment data is refreshed daily. AR status may not reflect payments made today. Please check again tomorrow.',
    back: 'Back',
    proceed: 'Proceed',
    startNew: 'Start New Request',

    step3Title: 'Registration Verification',
    step3Instruction: 'Select the business registration type based on the outlet registration number.',
    regType: 'Registration Type',
    ssmRegistered: 'SSM Registered',
    nonSsm: 'Non-SSM Registered',
    silaChoice: 'Please select...',
    ssmSubType: 'Registration Category',
    llp: 'LLP (Limited Liability Partnership)',
    nonSsmSubType: 'Entity Type',
    regNoLabel: 'Registration Number',
    regNoEdit: 'Update',
    regNoInput: 'Enter Registration Number',
    regNoRequired: 'Please enter a valid registration number before proceeding.',
    rocRobFormat: 'Accepted format: 12-digit number (e.g. 202401000123)',
    llpFormat: 'Accepted format: LLP0001234-LGN',
    nonSsmFormat: 'No fixed format required.',
    nonSsmDocNotice: 'Please prepare your business registration document for upload at a later stage.',
    verifyBtn: 'Submit',
    verifyingRegistration: 'Re-confirming registration status...',
    verifyFailed: 'Registration validation failed. Please go back and re-verify before proceeding.',
    ssmValidating: 'Details are being verified with SSM. This process may take up to 2 minutes. Please do not close, refresh the page, or click the back button.',
    skmValidating: 'Verifying record with SKM. Warning: This process may take up to 2 minutes. Please do not close, refresh the page, or press the back button.',
    portalRecord: 'Official SSM/SKM Record',
    officialName: 'Official Name',
    officialStatus: 'Official Status',
    portalDown: 'The official portal is currently unavailable and registration could not be verified automatically. By proceeding, you confirm that the registration number submitted is accurate and take full responsibility for its accuracy. The credit team will verify manually during assessment.',
    portalDownConfirm: 'I confirm that registration number {regno} is correct.',
    noRecord: 'No record found on the official portal for this registration number. Please verify before proceeding.',
    inactive: 'This entity\'s registration status is "{status}". Only active or existing entities are eligible. Please verify with the outlet before proceeding.',
    fuzzyFail: 'The registered company name does not match the outlet name in AMAST.\n\n- Registered name: "{officialName}"\n- Outlet name: "{outletName}"\n\nPlease verify the correct registration number before proceeding.',

    step4Title: 'Credit Request',
    newOutletSection: 'New Outlet Information',
    newOutletInstruction: 'This outlet has no prior trading history with us. Please provide the following details.',
    monthlyPurchase: 'Expected Monthly Purchase Volume (RM)',
    skuCount: 'Expected Count of Unique SKU',
    brands: 'Brands of Interest',
    monthlyPurchasePlaceholder: 'e.g. 5000',
    skuCountPlaceholder: 'e.g. 10',
    brandsPlaceholder: 'e.g. Zus, Samyang, Indomie',
    reqLimit: 'Requested Credit Limit (RM)',
    reqLimitPlaceholder: 'Enter requested limit',
    reqLimitBelowCurrent: 'Requested limit cannot be lower than the current credit limit of RM{limit}.',
    justificationLabel: 'Justification for Significant Increase',
    justificationHint: 'The requested limit represents a significant increase from the current limit. Please provide justification.',
    justificationPlaceholder: 'e.g. Changes in business activity, new product range, or supporting documents where relevant.',
    latePaymentSection: 'Late Payment Records',
latePaymentHint: '[!] The following invoices were settled with critical overdue days. For credit assessment purposes, remarks are required regarding the cause of past delays and the outlet\'s current situation.',
    latePaymentResponseLabel: 'Late Payment Response',
    latePaymentPlaceholder: 'e.g. Payment delays were due to cash flow issues which have since been resolved.',
    otherRemarks: 'Other Remarks',
    otherRemarksHint: 'e.g. Credit term request, outlet group affiliation, changes in business activity.',
    otherRemarksPlaceholder: 'Any additional remarks...',
    optional: '(Optional)',

    step5Title: 'Document Upload',
    bizRegDoc: 'Business Registration Document',
    bizRegDocHint: 'Required for Non-SSM entities (except Koperasi). Optional for SSM registered entities.',
    pdcDocs: 'PDC & Other Supporting Documents',
    pdcDocsHint: 'Optional. e.g. Site visit photos, PDC scans. Max 5 files.',
    financialDoc: 'Financial Document',
    financialDocHint: 'Optional. e.g. Audited accounts, bank statements. Max 3 files.',
    ctosConsent: 'CTOS Consent Form',
    ctosConsentHint: 'Optional. Download, sign, and upload.',
    downloadEN: 'Download (EN)',
    downloadBM: 'Download (BM)',
    chooseFiles: 'Choose file(s)',
    fileSizeError: 'Each file must be below 5MB.',
    totalSizeError: 'Total upload size must be below 12MB.',
    bizRegRequired: 'Business registration document is required for this entity type.',

    step6Title: 'Final Summary',
    registration: 'Registration',
    creditRequest: 'Credit Request',
    documents: 'Documents',
    edit: 'Edit',
    filesUploaded: '{n} file(s) uploaded',
    notUploaded: 'Not uploaded',
    submit: 'Submit Application',
    submitting: 'Submitting...',
    submitSuccess: 'Application submitted successfully. You will be notified once the assessment is complete.',
    submitError: 'Submission failed. Please try again.',
    dailyCapRequestorMsg: (cap) => `Today's submission quota (${cap} per day) has been fully utilized. Please try again tomorrow.`,
    dailyCapRegionMsg: (cap, region) => `Today's submission quota for region ${region} (${cap} per day) has been fully utilized. Please try again tomorrow.`,

    crossRegionTitle: 'Region Confirmation',
    crossRegionMsg: (r) => `This outlet falls under Region ${r}. Your application will be processed under that region's portfolio, but you can still monitor the status of this submission on your dashboard. Do you wish to proceed?`,
    crossRegionProceed: 'Yes, Proceed',
    crossRegionBack: 'No, Go Back',
    resubPendingTitle: 'Existing Pending Application',
    resubPendingMsg: (ref, date) => `This outlet already has a credit application in progress (Ref #${ref}, submitted ${date}). A new application cannot be submitted until the current application is decided.`,
    resubDeclinedTitle: 'Recently Declined Application',
    resubDeclinedMsg: (ref, date) => `This outlet's previous application (Ref #${ref}) was declined on ${date}. Please make sure the current appeal are supported with strong justification.`,
    resubApprovedTitle: 'Recently Approved Application',
    resubApprovedMsg: (ref, date) => `This outlet's previous application (Ref #${ref}) was approved on ${date}. Please ensure there is a valid business reason for this new submission.`,
    resubAppealExhaustedTitle: 'Appeal Already Submitted',
    resubAppealExhaustedMsg: (ref, date, reopenDate) => `This outlet's appeal (Ref #${ref}) was already submitted and declined on ${date}. No further resubmission is allowed until ${reopenDate}.`,
    resubJustLabel: 'Reason for Resubmission (minimum 100 characters)',
    resubJustPlaceholder: 'Explain what has changed since the previous application...',
    resubProceed: 'Proceed',
    resubBack: 'Back',
    resubCharCount: (n) => `${n}/100 characters`,
  },
  bm: {
    title: 'Sistem Permohonan Kredit',
    signOut: 'Log keluar',
    dashboard: 'Permohonan Saya',

    step1Title: 'Masukkan Outlet ID',
    outletIdPlaceholder: 'cth. NM2004676',
    checkBtn: 'Semak',
    checking: 'Sedang Disemak...',
    outletNotFound: 'Outlet ID tidak ditemui. ID mungkin salah dimasukkan, atau outlet baharu ini masih belum dikemas kini ke dalam sistem. Sila semak semula ID atau cuba lagi esok.',
    outletId: 'Outlet ID',
    outletName: 'Nama Outlet',
    siteId: 'Site ID',
    vanId: 'Van ID',
    creditLimit: 'Had Kredit',
    paymentTerm: 'Tempoh Kredit',
    regNo: 'Nombor Pendaftaran',
    confirmPrompt: 'Sila pastikan maklumat outlet di atas adalah betul sebelum meneruskan.',
    confirmYes: 'Ya, Teruskan',
    confirmNo: 'Tidak, Kembali',
    checkingRisk: 'Sila tunggu sebentar, rekod outlet sedang dimuat turun...',

    step2Title: 'Penilaian Risiko',
    blacklistCard: 'Status Senarai Hitam',
    blacklistNil: 'Tiada',
    blacklistHit: 'Disenaraihitamkan',
    arCard: 'Status AR Semasa',
    currentAR: 'Baki AR',
    totalOverdue: 'Jumlah Tertunggak',
    latePayCard: 'Sejarah Pembayaran Lewat',
    latePayNote: '[!] Rekod pembayaran lewat dikesan. Sila memberikan ulasan atau justifikasi di peringkat kemudian.',
    invoiceId: 'ID Invois',
    invoiceDate: 'Tarikh Invois',
    dueDate: 'Tarikh Matang',
    amount: 'Jumlah (RM)',
    dpd: 'Hari Tunggakan (DPD)',
    dpdLabel: 'Hari Tunggakan (DPD)',
    lastLimitCard: 'Sejarah Kredit',
    noLastLimit: 'Tiada rekod terdahulu.',
    finalLimit: 'Had Akhir',
    effectiveDate: 'Tarikh Berkuat Kuasa',
    decision: 'Keputusan',
    remark: 'Catatan',
    kycStatus: 'Status KYC',
    approver: 'Pegawai',
    source: 'Sumber',
    blacklistBlock: 'Outlet ini telah disenaraihitamkan. Permohonan tidak dibenarkan. Sila hubungi pengurus kredit anda untuk penjelasan.',
    overdueBlock: 'Outlet ini mempunyai invois tertunggak berjumlah RM{amount}. Sila pastikan kutipan penuh dilakukan sebelum menghantar semula.',
    dataDisclaimer: 'Nota: Maklumat pembayaran dikemas kini secara harian. Status AR mungkin belum dikemas kini jika bayaran baru dibuat hari ini. Sila semak semula esok.',
    back: 'Kembali',
    proceed: 'Teruskan',
    startNew: 'Mulakan Permohonan Baru',

    step3Title: 'Pengesahan Pendaftaran',
    step3Instruction: 'Pilih jenis pendaftaran perniagaan berdasarkan nombor pendaftaran outlet.',
    regType: 'Jenis Pendaftaran',
    ssmRegistered: 'Berdaftar dengan SSM',
    nonSsm: 'Bukan Berdaftar dengan SSM',
    silaChoice: 'Sila pilih...',
    ssmSubType: 'Kategori Pendaftaran',
    llp: 'LLP (Perkongsian Liabiliti Terhad)',
    nonSsmSubType: 'Jenis Entiti',
    regNoLabel: 'Nombor Pendaftaran',
    regNoEdit: 'Kemaskini',
    regNoInput: 'Masukkan Nombor Pendaftaran',
    regNoRequired: 'Sila masukkan nombor pendaftaran yang sah sebelum meneruskan.',
    rocRobFormat: 'Format yang diterima: 12 digit nombor (cth. 202401000123)',
    llpFormat: 'Format yang diterima: LLP0001234-LGN',
    nonSsmFormat: 'Tiada format tetap diperlukan.',
    nonSsmDocNotice: 'Sila sediakan bukti borang pendaftaran untuk dimuat naik kemudian.',
    verifyBtn: 'Semak',
    verifyingRegistration: 'Pengesahan semula status pendaftaran...',
    verifyFailed: 'Status pendaftaran gagal disahkan. Sila kembali dan ulang semula pengesahan sebelum meneruskan.',
    ssmValidating: 'Butiran sedang disemak dengan SSM. Proses ini mungkin mengambil masa sehingga 2 minit. Sila jangan tutup, muat semula halaman, atau klik butang kembali.',
    skmValidating: 'Rekod sedang disemak dengan SKM. Amaran: Proses ini mungkin mengambil masa sehingga 2 minit. Sila jangan tutup, muat semula halaman, atau tekan butang kembali.',
    portalRecord: 'Rekod Rasmi SSM/SKM',
    officialName: 'Nama Rasmi',
    officialStatus: 'Status Rasmi',
    portalDown: 'Portal rasmi tidak tersedia buat masa ini dan pendaftaran tidak dapat disahkan secara automatik. Dengan meneruskan, anda mengesahkan bahawa nombor pendaftaran yang dikemukakan adalah tepat dan bertanggungjawab sepenuhnya atas ketepatannya.',
    portalDownConfirm: 'Saya mengesahkan bahawa nombor pendaftaran {regno} adalah betul.',
    noRecord: 'Tiada rekod dijumpai di portal rasmi untuk nombor pendaftaran ini. Sila semak sebelum meneruskan.',
    inactive: 'Status pendaftaran entiti ini ialah "{status}". Hanya entiti aktif atau sedia ada yang layak. Sila semak dengan outlet sebelum meneruskan.',
    fuzzyFail: 'Nama syarikat berdaftar tidak sepadan dengan nama outlet dalam AMAST.\n\n- Nama berdaftar: "{officialName}"\n- Nama Outlet: "{outletName}"\n\nSila semak nombor pendaftaran yang betul sebelum meneruskan.',

    step4Title: 'Permohonan Kredit',
    newOutletSection: 'Maklumat Outlet Baru',
    newOutletInstruction: 'Outlet ini tiada rekod transaksi terdahulu. Sila lengkapkan maklumat (*) di bawah untuk penilaian kredit.',
    monthlyPurchase: 'Anggaran Nilai Pembelian Bulanan (RM)',
    skuCount: 'Anggaran Jumlah SKU Baharu',
    brands: 'Jenama Sasaran Utama',
    monthlyPurchasePlaceholder: 'cth. 5000',
    skuCountPlaceholder: 'cth. 10',
    brandsPlaceholder: 'cth. Zus, Samyang, Indomie',
    reqLimit: 'Had Kredit yang Dipohon (RM)',
    reqLimitPlaceholder: 'Masukkan had yang diminta',
    reqLimitBelowCurrent: 'Had yang diminta tidak boleh lebih rendah daripada had kredit semasa RM{limit}.',
    justificationLabel: 'Justifikasi Peningkatan Ketara',
    justificationHint: 'Had kredit yang dipohon melibatkan peningkatan ketara daripada had sedia ada. Sila nyatakan justifikasi permohonan.',
    justificationPlaceholder: 'cth. Perubahan dalam aktiviti perniagaan, rangkaian produk baru.',
    latePaymentSection: 'Rekod Pembayaran Lewat',
latePaymentHint: '[!] Invois berikut telah dijelaskan dengan hari tunggakan yang kritikal. Bagi tujuan penilaian kredit, ulasan diperlukan berkenaan punca kelewatan lalu serta situasi semasa outlet.',
    latePaymentResponseLabel: 'Respons Pembayaran Lewat',
    latePaymentPlaceholder: 'cth. Kelewatan pembayaran disebabkan isu aliran tunai yang telah diselesaikan.',
    otherRemarks: 'Catatan Tambahan',
    otherRemarksHint: 'Contoh: Permohonan tempoh kredit (credit term), maklumat syarikat induk (HQ/Holding), atau rujukan akaun sedia ada.',
    otherRemarksPlaceholder: 'Sebarang catatan tambahan...',
    optional: '(Tidak Wajib)',

    step5Title: 'Muat Naik Dokumen',
    bizRegDoc: 'Borang Pendaftaran',
    bizRegDocHint: 'Wajib untuk entiti bukan SSM (kecuali Koperasi). Tidak Wajib untuk entiti berdaftar SSM.',
    pdcDocs: 'PDC & Dokumen Sokongan Lain',
    pdcDocsHint: 'Tidak Wajib. cth. Foto lawatan tapak, imbasan PDC. Maks 5 fail.',
    financialDoc: 'Penyata Audit / Penyata Bank',
    financialDocHint: 'Tidak Wajib. cth. Akaun beraudit, penyata bank. Maks 3 fail.',
    ctosConsent: 'Borang Permohonan CTOS',
    ctosConsentHint: 'Tidak Wajib. Muat turun, tandatangan, dan muat naik.',
    downloadEN: 'Muat Turun (BI)',
    downloadBM: 'Muat Turun (BM)',
    chooseFiles: 'Pilih fail',
    fileSizeError: 'Setiap fail mesti kurang daripada 5MB.',
    totalSizeError: 'Jumlah saiz muat naik mesti kurang daripada 12MB.',
    bizRegRequired: 'Dokumen pendaftaran perniagaan diperlukan untuk jenis entiti ini.',

    step6Title: 'Ringkasan Akhir',
    registration: 'Pendaftaran',
    creditRequest: 'Permohonan Kredit',
    documents: 'Dokumen',
    edit: 'Ubah',
    filesUploaded: '{n} fail dimuat naik',
    notUploaded: 'Tidak dimuat naik',
    submit: 'Hantar Permohonan',
    submitting: 'Menghantar...',
    submitSuccess: 'Permohonan berjaya dihantar. Anda akan dimaklumkan setelah penilaian selesai.',
    submitError: 'Penghantaran gagal. Sila cuba lagi.',
    dailyCapRequestorMsg: (cap) => `Kuota permintaan hari ini (${cap} sehari) telah pun digunakan sepenuhnya. Sila cuba lagi esok.`,
    dailyCapRegionMsg: (cap, region) => `Kuota permintaan hari ini untuk region ${region} (${cap} sehari) telah pun digunakan sepenuhnya. Sila cuba lagi esok.`,

    crossRegionTitle: 'Pengesahan Wilayah',
    crossRegionMsg: (r) => `Outlet ini tertakluk di bawah Region ${r}. Permohonan ini akan diproses di bawah portfolio region tersebut, namun anda masih boleh menyemak status permohonan ini di dashboard anda. Adakah anda ingin meneruskan?`,
    crossRegionProceed: 'Ya, Teruskan',
    crossRegionBack: 'Tidak, Kembali',
    resubPendingTitle: 'Permohonan Dalam Proses',
    resubPendingMsg: (ref, date) => `Outlet ini sudah mempunyai permohonan kredit yang sedang diproses (Ruj #${ref}, dihantar pada ${date}). Permohonan baharu tidak boleh dihantar semula sehinggalah keputusan disalurkan.`,
    resubDeclinedTitle: 'Permohonan Ditolak Baru-baru Ini',
    resubDeclinedMsg: (ref, date) => `Permohonan terdahulu untuk outlet ini (No Rujukan #${ref}) telah ditolak pada ${date}. Sila pastikan pemohonan rayuan ini disokong dengan bukti yang lebih kukuh.`,
    resubApprovedTitle: 'Permohonan Diluluskan Baru-baru Ini',
    resubApprovedMsg: (ref, date) => `Permohonan terdahulu untuk outlet ini (No Rujukan #${ref}) telah diluluskan pada ${date}. Sila pastikan pemohonan ini disokong dengan sebab-sebab yang munasabah.`,
    resubAppealExhaustedTitle: 'Rayuan Sudah Dihantar',
    resubAppealExhaustedMsg: (ref, date, reopenDate) => `Rayuan untuk outlet (Ruj #${ref}) telah pun dihantar dan ditolak pada ${date}. Penghantaran semula tidak dibenarkan sehingga ${reopenDate}.`,
    resubJustLabel: 'Sebab Permohonan Semula (minimum 100 aksara)',
    resubJustPlaceholder: 'Jelaskan apa yang telah berubah sejak permohonan terdahulu...',
    resubProceed: 'Teruskan',
    resubBack: 'Kembali',
    resubCharCount: (n) => `${n}/100 aksara`,
  },
};

// -- Amount formatter -----------------------------------------
function fmtAmt(n) {
  if (n == null) return '-';
  const num = Number(n);
  const neg = num < 0;
  const abs = Math.abs(num);
  const parts = abs.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + parts[0] + '-' + parts[1];
}

function fmtRM(n) {
  if (n == null) return '-';
  const num = Number(n);
  const neg = num < 0;
  const abs = Math.abs(num);
  const parts = abs.toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + 'RM ' + parts[0] + '-' + parts[1];
}

// -- Tint colours per card type --------------------------------
const TINT = {
  green:  { bg: '#f0faf3', border: '#b7dfbf', titleColor: '#1e7e34' },
  orange: { bg: '#fff8ed', border: '#ffd99a', titleColor: '#b06000' },
  red:    { bg: '#fff0f0', border: '#ffc5c5', titleColor: '#c8102e' },
  neutral:{ bg: '#fff',    border: '#e0e0e0', titleColor: '#555' },
};

function tintCard(type) {
  const t = TINT[type] || TINT.neutral;
  return { background: t.bg, border: `0.5px solid ${t.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 10 };
}

function tintTitle(type) {
  const t = TINT[type] || TINT.neutral;
  return { fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: t.titleColor, marginBottom: 10, paddingBottom: 6, borderBottom: `0.5px solid ${t.border}` };
}

// -- Spinner ---------------------------------------------------
function Spinner({ size = 20, color = '#c8102e' }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      border: `2.5px solid rgba(0,0,0,0.12)`, borderTopColor: color,
      borderRadius: '50%', animation: 'spin 0.7s linear infinite',
      verticalAlign: 'middle', flexShrink: 0,
    }} />
  );
}

// -- Loading popup ---------------------------------------------
function LoadingPopup({ message }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '0 24px',
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: '24px 20px',
        maxWidth: 360, width: '100%', textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <Spinner size={32} />
        <div style={{ marginTop: 14, fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{message}</div>
      </div>
    </div>
  );
}

// -- Styles ----------------------------------------------------
const s = {
  page:        { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'system-ui,-apple-system,sans-serif' },
  nav:         { background: '#c8102e', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  navL:        { display: 'flex', alignItems: 'center', gap: 12 },
  navT:        { color: '#fff', fontWeight: 700, fontSize: 15 },
  navR:        { display: 'flex', alignItems: 'center', gap: 10 },
  langBtn:     { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', padding: '3px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  dashBtn:     { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  logoutB:     { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 },

  // Sticky outlet card
  stickyWrap:  { position: 'sticky', top: 48, zIndex: 90, padding: '8px 16px 0', maxWidth: 640, margin: '0 auto' },
  stickyCard:  { background: '#fff', borderRadius: 10, boxShadow: '0 3px 10px rgba(0,0,0,0.12)', overflow: 'hidden', border: '1px solid #ddd' },
  stickyBar:   { padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' },
stickyBarL:  { display: 'flex', alignItems: 'center', gap: 10 },
stickyId:    { fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
stickyName:  { fontSize: 13, color: '#555', fontWeight: 400 },
  stickyToggle:{ fontSize: 11, color: '#c8102e', fontWeight: 600 },
  stickyBody:  { padding: '0 16px 14px', borderTop: '0.5px solid #f0f0f0' },

  body:        { maxWidth: 640, margin: '0 auto', padding: '12px 16px 32px' },
  card:        { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: 20, marginBottom: 12 },
  cardTitle:   { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 14 },
  label:       { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' },
  input:       { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' },
  textarea:    { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical', minHeight: 80 },
  select:      { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', background: '#fff' },
  grid2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' },
  kv:          { marginBottom: 0 },
  kvLabel:     { fontSize: 11, color: '#888', marginBottom: 2 },
  kvValue:     { fontSize: 13, fontWeight: 600, color: '#1a1a2e' },
  actions:     { display: 'flex', gap: 10, marginTop: 16 },
  btnPrimary:  { flex: 1, padding: '12px 0', background: '#c8102e', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGreen:    { flex: 1, padding: '12px 0', background: '#1e7e34', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnGrey:     { flex: 1, padding: '12px 0', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary:{ flex: 1, padding: '12px 0', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDanger:   { flex: 1, padding: '12px 0', background: '#fff', color: '#c5221f', border: '1px solid #c5221f', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  bannerRed:   { background: '#fff0f0', border: '0.5px solid #ffc5c5', borderRadius: 8, padding: '12px 14px', color: '#c5221f', fontSize: 13, marginBottom: 10, lineHeight: 1.5 },
  bannerYellow:{ background: '#fff8ed', border: '0.5px solid #ffd99a', borderRadius: 8, padding: '12px 14px', color: '#7a5900', fontSize: 13, marginBottom: 10, lineHeight: 1.5 },
  bannerGreen: { background: '#f0faf3', border: '0.5px solid #b7dfbf', borderRadius: 8, padding: '12px 14px', color: '#1e7e34', fontSize: 13, marginBottom: 10, lineHeight: 1.5 },
  bannerBlue:  { background: '#e8f2fd', border: '0.5px solid #b3d4f5', borderRadius: 8, padding: '12px 14px', color: '#0d47a1', fontSize: 13, marginBottom: 10, lineHeight: 1.5 },
  divider:     { height: '0.5px', background: '#f0f0f0', margin: '12px 0' },
  hint:        { fontSize: 12, color: '#888', marginTop: 4, marginBottom: 8, lineHeight: 1.4 },
  err:         { color: '#c5221f', fontSize: 12, marginTop: 4 },
  fileBtn:     { display: 'inline-block', padding: '8px 14px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th:          { padding: '7px 10px', background: 'rgba(0,0,0,0.04)', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '0.5px solid #eee' },
  td:          { padding: '7px 10px', borderBottom: '0.5px solid #f5f5f5', color: '#333' },
  badge:       (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: color + '22', color }),
  checkbox:    { display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12 },
  checkboxI:   { marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 },
  checkboxL:   { fontSize: 13, color: '#555', lineHeight: 1.5 },
  sectionHead: { fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 8, marginTop: 4 },
};

function KV({ label, value, full }) {
  return (
    <div style={full ? { gridColumn: '1/-1', ...s.kv } : s.kv}>
      <div style={s.kvLabel}>{label}</div>
      <div style={s.kvValue}>{value || '-'}</div>
    </div>
  );
}

// -- Sticky Outlet Card ----------------------------------------
function StickyOutletCard({ outlet, t, collapsed, setCollapsed, lang }) {
  if (!outlet) return null;
  return (
    <div style={s.stickyWrap}>
      <div style={s.stickyCard}>
        <div style={s.stickyBar} onClick={() => setCollapsed(c => !c)}>
          <div style={s.stickyBarL}>
            <div style={s.stickyId}>{outlet.outletId}</div>
            <div style={{ width: 1, height: 14, background: '#ccc' }} />
            <div style={s.stickyName}>{outlet.outletName}</div>
          </div>
          <div style={s.stickyToggle}>{collapsed ? (lang === 'bm' ? '+ Paparkan' : '+ Expand') : (lang === 'bm' ? '- Sembunyikan' : '- Collapse')}</div>
        </div>
        {!collapsed && (
          <div style={s.stickyBody}>
            <div style={{ ...s.grid2, marginTop: 10 }}>
              <KV label={t.siteId}      value={outlet.siteId} />
              <KV label={t.vanId}       value={outlet.vanId} />
              <KV label={t.creditLimit} value={outlet.creditLimit != null ? fmtRM(outlet.creditLimit) : '-'} />
              <KV label={t.paymentTerm} value={outlet.paymentTerm ? (lang === 'bm' ? outlet.paymentTerm.replace(/days?/i, 'hari') : outlet.paymentTerm) : '-'} />
              <KV label={t.regNo}       value={outlet.ssm || '-'} />
              <KV label={lang === 'en' ? 'Region' : 'Region'} value={outlet.van_region || '-'} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// -- n8n webhook -----------------------------------------------
// n8n base URL � set N8N_BASE_URL in your .env (same value as on the n8n server).
// Production webhook path is always /webhook/cas-submission.
const N8N_WEBHOOK_URL = '/api/cas/notify-n8n';
const WEBHOOK_QUEUE_KEY = 'cas_pending_webhooks';

async function _fireWebhookOnce(payload) {
  const res = await fetch(N8N_WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`n8n webhook HTTP ${res.status}`);
}

// Fire webhook, or queue it in localStorage for retry on the next page load
// if n8n is temporarily unreachable (network blip, n8n restart, etc.).
async function queueOrFireWebhook(payload) {
  try {
    await _fireWebhookOnce(payload);
  } catch (e) {
    console.warn('[CASPage] n8n webhook failed � queued for retry:', e.message);
    try {
      const queue = JSON.parse(localStorage.getItem(WEBHOOK_QUEUE_KEY) || '[]');
      queue.push({ payload, timestamp: Date.now(), attempts: 1 });
      localStorage.setItem(WEBHOOK_QUEUE_KEY, JSON.stringify(queue));
    } catch { /* localStorage unavailable � silently drop */ }
  }
}

// Drain the pending-webhook queue on page load.
// Each item gets up to MAX_ATTEMPTS tries across page loads;
// after that it is dropped so stale entries don't pile up forever.
const MAX_WEBHOOK_ATTEMPTS = 3;
async function drainWebhookQueue() {
  let queue;
  try {
    queue = JSON.parse(localStorage.getItem(WEBHOOK_QUEUE_KEY) || '[]');
  } catch { return; }
  if (!queue.length) return;
  const remaining = [];
  for (const item of queue) {
    try {
      await _fireWebhookOnce(item.payload);
      // success � don't push back
    } catch {
      if (item.attempts < MAX_WEBHOOK_ATTEMPTS) {
        remaining.push({ ...item, attempts: item.attempts + 1 });
      }
      // else drop � exceeded retry budget
    }
  }
  try {
    localStorage.setItem(WEBHOOK_QUEUE_KEY, JSON.stringify(remaining));
  } catch { /* ignore */ }
}

// -- Main ------------------------------------------------------
const CAS_RSH_EMAILS = ['rosvinder.kaur@ninjavan.co', 'afiq.hadi@ninjavan.co'];
  const CAS_HEAD_OF_SALES_EMAILS = ['dwayne.tung@ninjavan.co'];
function getCASUserTitle(email, role) {
  if (role === 'kyc') return 'KYC Admin';
  if (CAS_HEAD_OF_SALES_EMAILS.includes(email)) return 'Deputy Head, Ninja Mart';
  return CAS_RSH_EMAILS.includes(email) ? 'Regional Sales Head' : 'Regional Sales Supervisor';
}
function CASSidebar({ open, onClose, navigate, lang, role }) {
  const { user } = useAuth();
  const [showMaintPanel, setShowMaintPanel] = useState(false);
  if (!open) return null;
  const isAdmin = MAINTENANCE_ADMINS.includes((user?.email || '').toLowerCase());
  // KYC is a special role � it can submit test applications through CAS,
  // but AR Summary and the Credit Applications dashboard are sales-specific
  // views that don't apply to it. Route it back to the main Queue instead.
  const items = role === 'kyc'
    ? [
        { label: lang === 'bm' ? 'KYC Assessment' : 'KYC Assessment', path: '/', active: false },
      ]
    : [
        { label: lang === 'bm' ? 'Ringkasan Kredit & Tunggakan' : 'AR Summary',          path: '/cas/ar',       active: false },
        { label: lang === 'bm' ? 'Semakan Permohonan Kredit'    : 'Credit Applications', path: '/cas/dashboard', active: false },
        { label: lang === 'bm' ? 'Penilaian Kredit Bulanan'       : 'Monthly Review',      path: '/cas/monthly-review', active: false },
      ];
  const allItems = [...items, ...(isAdmin ? [{ label: 'Maintenance Mode', path: '__maintenance__', active: false }] : [])];
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300 }} onClick={onClose} />
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 240, background: '#fff', zIndex: 400, boxShadow: '4px 0 16px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#c8102e', padding: '20px 16px 16px', color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Ninja Mart Credit</div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Sales Portal</div>
        </div>
        <div style={{ flex: 1, padding: '12px 0' }}>
          {allItems.map(item => (
            <div key={item.path}
              style={{ padding: '12px 20px', cursor: 'pointer', fontSize: 13, fontWeight: item.active ? 700 : 400, color: item.active ? '#c8102e' : '#555', background: item.active ? '#fff0f0' : 'transparent', borderLeft: item.active ? '3px solid #c8102e' : '3px solid transparent' }}
              onClick={e => {
                e.stopPropagation();
                if (item.path === '__maintenance__') { setShowMaintPanel(true); return; }
                navigate(item.path); onClose();
              }}>
              {item.label}
            </div>
          ))}
        </div>
      </div>
      {showMaintPanel && <MaintenanceTogglePanel onClose={() => setShowMaintPanel(false)} />}
    </>
  );
}
export default function CASPage() {
  const { user, logout } = useAuth();
  const maint = useMaintenanceCheck('sales');
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('cas_lang') || 'en');
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const t = LANG[lang];

  // Prevents concurrent handleSubmit calls (React state updates are async,
  // so the disabled prop on the button isn't guaranteed to stop a fast double-tap).
  const submittingRef = useRef(false);
  // Same guard for SSM/SKM portal validation � the verify button is conditionally
  // hidden while portalState === 'validating', but that's React state, not sync.
  // A fast double-tap before the re-render can fire two simultaneous portal calls.
  const portalValidatingRef = useRef(false);

  useEffect(() => {
    if (!profileOpen) return;
    const close = () => setProfileOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [profileOpen]);

  // Retry any webhook calls that failed on a previous submit
  useEffect(() => { drainWebhookQueue(); }, []);

  function toggleLang() {
    const next = lang === 'en' ? 'bm' : 'en';
    setLang(next); localStorage.setItem('cas_lang', next);
  }

  // -- State --------------------------------------------------
  const [step, setStep]             = useState(0);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState('');
  const [outletCardCollapsed, setOutletCardCollapsed] = useState(false);

  // Step 1
  const [outletInput, setOutletInput]       = useState('');
  const [outlet, setOutlet]                 = useState(null);
  const [outletNotFound, setOutletNotFound] = useState(false);

  // Step 2
  const [riskLoading, setRiskLoading] = useState(false);
  const [risk, setRisk]               = useState(null);
  const [riskOutletId, setRiskOutletId] = useState(null);

  // Cross-region
  const [crossRegionPopup, setCrossRegionPopup]     = useState(false);
  const [pendingRiskAfterRegion, setPendingRiskAfterRegion] = useState(false);

  // Resubmission
  const [resubmissionPopup, setResubmissionPopup]   = useState(null);
  const [resubmitJustification, setResubmitJustification] = useState('');

  // Step 3
  const [regCat, setRegCat]               = useState('');
  const [regSub, setRegSub]               = useState('');
  const [regNo, setRegNo]                 = useState('');
  const [editRegNo, setEditRegNo]         = useState(false);
  const [portalState, setPortalState]     = useState('');
  const [portalResult, setPortalResult]   = useState(null);
  const [portalBlockMsg, setPortalBlockMsg] = useState('');
  const [portalConfirmed, setPortalConfirmed] = useState(false);
  const [step3Verifying, setStep3Verifying]   = useState(false);

  // Step 4
  const [monthlyPurchase, setMonthlyPurchase] = useState('');
  const [skuCount, setSkuCount]               = useState('');
  const [brands, setBrands]                   = useState('');
  const [reqLimit, setReqLimit]               = useState('');
  const [justification, setJustification]     = useState('');
  const [latePayResp, setLatePayResp]         = useState('');
  const [otherRemarks, setOtherRemarks]       = useState('');

  // Step 5
  const [bizRegFiles, setBizRegFiles]       = useState([]);
  const [pdcFiles, setPdcFiles]             = useState([]);
  const [financialFiles, setFinancialFiles] = useState([]);
  const [ctosFiles, setCtosFiles]           = useState([]);

  // -- Helpers -----------------------------------------------
  function resetAll() {
    setStep(0); setBusy(false); setErr('');
    setOutletInput(''); setOutlet(null); setOutletNotFound(false);
    setRisk(null); setRiskOutletId(null); setRiskLoading(false); setOutletCardCollapsed(false);
    setResubmissionPopup(null); setResubmitJustification('');
    setRegCat(''); setRegSub(''); setRegNo(''); setEditRegNo(false);
    setPortalState(''); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false);
    setMonthlyPurchase(''); setSkuCount(''); setBrands('');
    setReqLimit(''); setJustification(''); setLatePayResp(''); setOtherRemarks('');
    setBizRegFiles([]); setPdcFiles([]); setFinancialFiles([]); setCtosFiles([]);
  }

  // Reset to clean state every time the page loads � prevents stale outlet
  // data from a previous session showing up on return.
  useEffect(() => { resetAll(); }, []);

  function needsJustification() {
    const req  = parseFloat(reqLimit)  || 0;
    const curr = parseFloat(outlet?.creditLimit) || 0;
    const isNew = !risk?.hasRepaymentHistory || !curr;
    if (isNew)       return req >= 3000;
    if (curr < 5000) return req >= curr * 2;
    return req >= curr * 1.5;
  }

  function regNoValid() {
    if (!regNo.trim()) return false;
    if (regCat === 'SSM') {
      if (regSub === 'ROC' || regSub === 'ROB') return /^\d{12}$/.test(regNo.trim());
      if (regSub === 'LLP')     return /^LLP\d{7}-LGN$/i.test(regNo.trim());
    }
    return regNo.trim().length > 0;
  }

  const needsPortal = regCat === 'SSM' || regSub === 'KOPERASI';
  const canProceedStep3 = (
    regNoValid() && regCat && regSub &&
    (!needsPortal || portalState === 'success' || (portalState === 'down' && portalConfirmed))
  );

  // -- Step 1 ------------------------------------------------
  async function handleCheck() {
    const id = outletInput.trim().toUpperCase();
    if (!id) return;
    setBusy(true); setErr(''); setOutletNotFound(false); setOutlet(null);
    try {
      const res  = await fetch(`/api/cas/outlet/${id}`);
      const data = await res.json();
      if (!data.found) setOutletNotFound(true);
      else {
        const _unk = !String(data.van_region || '').trim() || String(data.van_region).trim().toLowerCase() === 'unknown';
        const _mine = regionsOf(user);
        if (_unk && _mine.length === 1) { data.van_region = _mine[0]; data.regionPicked = true; }
        setOutlet(data); setRegNo(data.ssm || '');
      }
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  // -- Step 2 ------------------------------------------------
  async function handleRiskCheck(skipRegionCheck = false) {
    // Region must be known (outlet with no van -> user picks it)
    if (!String(outlet.van_region || '').trim() || String(outlet.van_region).trim().toLowerCase() === 'unknown') {
      setErr('Please select the outlet region before continuing.');
      return;
    }
    // Cross-region check first
        if (!skipRegionCheck && outlet.van_region && user.region) {
      const myRegions = Array.isArray(user.region) ? user.region : [user.region];
      if (!myRegions.includes(outlet.van_region.trim())) {
        setCrossRegionPopup(true);
        return;
      }
    }
    if (risk && riskOutletId === outlet.outletId) {
      if (risk.resubmission) { setResubmissionPopup(risk.resubmission); }
      else { setOutletCardCollapsed(true); setStep(2); }
      return;
    }
    setRiskLoading(true); setErr('');
    try {
      const res  = await fetch(`/api/cas/risk/${outlet.outletId}`);
      const data = await res.json();
      setRisk(data);
      setRiskOutletId(outlet.outletId);
      if (data.resubmission) {
        setResubmissionPopup(data.resubmission);
        setResubmitJustification('');
      } else {
        setOutletCardCollapsed(true);
        setStep(2);
      }
    } catch (e) { setErr(e.message); }
    setRiskLoading(false);
  }

  function handleResubmitProceed() {
    setResubmissionPopup(null);
    setOutletCardCollapsed(true);
    setStep(2);
  }

  function handleResubmitBack() {
    setResubmissionPopup(null);
    setRisk(null);
  }

  // -- Step 3 portal -----------------------------------------
  async function handlePortal() {
    // Guard against double-tap: portalState='validating' hides the button, but
    // that's React state � a fast double-tap fires before the re-render.
    if (portalValidatingRef.current) return;
    portalValidatingRef.current = true;
    setPortalState('validating'); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false);
    try {
      let data;
      if (regCat === 'SSM') {
        const res = await fetch('/api/cas/validate-ssm', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ regno: regNo.trim(), registrationType: regSub, outletName: outlet.outletName }),
        });
        data = await res.json();
      } else {
        const res = await fetch('/api/cas/validate-skm', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ regno: regNo.trim(), outletName: outlet.outletName }),
        });
        data = await res.json();
      }
      if (data.success) {
        setPortalResult({ officialName: data.officialName, officialStatus: data.officialStatus || data.registrationDate, matchedRegistrationType: data.matchedRegistrationType || null });
        setPortalState('success');
      } else {
        // Could not get an answer (not a rejection) - keep in sync with COULDNT_GET_ANSWER in server.js
        if (['PORTAL_DOWN', 'ERROR', 'CAPSOLVER_FAILED', 'PORTAL_GLITCH', 'MISSING_CAPSOLVER_API_KEY', 'UNCAUGHT_EXCEPTION', 'UNHANDLED_REJECTION'].includes(data.reason)) setPortalState('down');
        else if (data.reason === 'NO_RECORD') { setPortalState('blocked'); setPortalBlockMsg(t.noRecord); }
        else if (data.reason === 'INACTIVE') { setPortalState('blocked'); setPortalBlockMsg(t.inactive.replace('{status}', data.officialStatus || '')); }
        else if (data.reason === 'FUZZY_FAIL') { setPortalState('blocked'); setPortalBlockMsg(t.fuzzyFail.replace('{officialName}', data.officialName || '').replace('{outletName}', outlet.outletName || '')); }
        else { setPortalState('blocked'); setPortalBlockMsg(data.error || 'Validation failed.'); }
      }
    } catch (e) {
      setPortalState('down');
    } finally {
      portalValidatingRef.current = false;
    }
  }

  // Re-confirms the logged check still matches what's on screen right now -
  // catches state drift if the user went back and changed regno/category
  // without the portal check re-running. SSM path only (regCat === 'SSM') -
  // SKM/Koperasi checks aren't logged anywhere yet to verify against.
  async function handleProceedStep3() {
    if (!canProceedStep3) return;
    if (regCat !== 'SSM') { setStep(4); return; }
    setStep3Verifying(true); setErr('');
    try {
      const res = await fetch(`/api/cas/verify-registration?regno=${encodeURIComponent(regNo.trim())}`);
      const data = await res.json();
      if (!data.validated) { setErr(t.verifyFailed); return; }
      setStep(4);
    } catch (e) {
      setErr(t.verifyFailed);
    } finally {
      setStep3Verifying(false);
    }
  }

  // -- File handling -----------------------------------------
  function handleFiles(e, setter, maxFiles) {
    const files = Array.from(e.target.files);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { setErr(t.fileSizeError); e.target.value = ''; return; }
    }
    setter(prev => [...prev, ...files].slice(0, maxFiles));
    e.target.value = '';
  }

  // -- Submit ------------------------------------------------
  async function handleSubmit() {
    // Guard against concurrent submissions (e.g. fast double-tap on mobile).
    // React state updates are async, so `disabled={busy}` alone isn't enough.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true); setErr('');

    try {
      const allFiles = [...bizRegFiles, ...pdcFiles, ...financialFiles, ...ctosFiles];
      if (allFiles.reduce((s, f) => s + f.size, 0) > 12 * 1024 * 1024) {
        setErr(t.totalSizeError); return;
      }
      if (regCat === 'NON' && regSub !== 'KOPERASI' && bizRegFiles.length === 0) {
        setErr(t.bizRegRequired); return;
      }

      async function toBase64(f) {
        return new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result.split(',')[1]);
          r.onerror = rej;
          r.readAsDataURL(f);
        });
      }
      async function encFiles(files, docType) {
        const out = [];
        for (const f of files) out.push({ docType, filename: f.name, mimeType: f.type, data: await toBase64(f) });
        return out;
      }
      const files = [
        ...(await encFiles(bizRegFiles,    'bizReg')),
        ...(await encFiles(pdcFiles,       'pdc')),
        ...(await encFiles(financialFiles, 'financial')),
        ...(await encFiles(ctosFiles,      'ctos')),
      ];

      // New lead vs existing outlet � same logic already used in
      // needsJustification() above, recomputed fresh here since it's not
      // stored in state. "New" means no prior repayment history OR no
      // existing credit facility; matches what the outlet has actually
      // done, not just what CASPage form fields say.
      const isNewLead = !risk?.hasRepaymentHistory || !parseFloat(outlet?.creditLimit);

      const res = await fetch('/api/cas/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outletId: outlet.outletId, outletName: outlet.outletName,
          submitterEmail: user.email,
                    outletRegion: outlet.van_region || '',
          outletStatus: isNewLead ? 'New' : 'Existing',
          entityType: regCat === 'SSM' ? (regSub === 'LLP' ? 'LLP' : 'ROC/ROB') : regSub,
          regno: regNo.trim(),
          requestedLimit: parseFloat(reqLimit),
          expectedMonthlyPurchase: monthlyPurchase ? parseFloat(monthlyPurchase) : '',
          expectedSkuCount:        skuCount        ? parseInt(skuCount)          : '',
          brandsOfInterest:        brands,
          salesRemarks: {
            limitJustification:   justification,
            latePaymentResponse:  latePayResp,
            other_remarks:        otherRemarks,
            portal_verified:      portalState === 'success',
            portal_skip_reason:   portalState === 'down' ? 'PORTAL_DOWN' : '',
            // Which SSM registration-type branch actually matched (Business /
            // Company / LLP) � needed downstream by ctos_report.js to pick the
            // correct Constitution in CTOS's Validate flow without guessing.
            // Only meaningful when portal_verified is true; null otherwise.
            matched_registration_type: portalState === 'success' ? (portalResult?.matchedRegistrationType || null) : null,
            is_resubmission:      !!risk?.resubmission,
            previous_ref:         risk?.resubmission?.mcRow || '',
            resubmission_reason:  resubmitJustification || '',
          },
          files,
        }),
      });
      const data = await res.json();
      if (!data.success) {
        const err = new Error(data.error);
        err.reason = data.reason;
        err.cap = data.cap;
        err.region = data.region;
        throw err;
      }

      // -- Trigger n8n flow (fire-and-forget) ------------------
      // n8n's Webhook node only expects { row_number: N } � it reads the full
      // submission from Google Sheets by row number. The backend's /api/cas/submit
      // handler writes the sheet row and returns the row_number (aliased as
      // refNo / ref / rowNumber depending on the API version).
      // If the webhook call fails (n8n down, network blip), queueOrFireWebhook
      // stores it in localStorage and drainWebhookQueue() retries on next page load.
      const rowNumber = data.row_number ?? data.rowNumber ?? data.refNo ?? data.ref ?? data.id;
      if (rowNumber != null) {
        queueOrFireWebhook({ row_number: rowNumber });
      }

      resetAll();
      navigate('/cas/dashboard');

    } catch (e) {
      if (e.reason === 'DAILY_CAP_REQUESTOR') {
        setErr(t.dailyCapRequestorMsg(e.cap));
      } else if (e.reason === 'DAILY_CAP_REGION') {
        setErr(t.dailyCapRegionMsg(e.cap, e.region));
      } else {
        setErr(t.submitError + ' ' + (e.message || ''));
      }
    } finally {
      // Always release the lock � even if navigate() was called.
      // React 18 handles state updates after navigation gracefully.
      submittingRef.current = false;
      setBusy(false);
    }
  }

  const isBlocked = risk && (risk.isBlacklisted || risk.isOverdue);

  function FileField({ label, hint, files, setter, maxFiles, id, children }) {
    return (
      <div style={{ marginBottom: 16 }}>
        <label style={s.label}>{label}</label>
        <div style={s.hint}>{hint}</div>
        {children}
        <label htmlFor={id} style={s.fileBtn}>{t.chooseFiles}</label>
        <input id={id} type="file" multiple={maxFiles > 1} accept="image/*,.pdf" style={{ display: 'none' }}
          onChange={e => handleFiles(e, setter, maxFiles)} />
        {files.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>
            {files.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginTop: 4, alignItems: 'center' }}>
                <span>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                <span style={{ cursor: 'pointer', color: '#c5221f', fontSize: 11 }}
                  onClick={() => setter(prev => prev.filter((_, j) => j !== i))}>x</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const showStickyCard = step >= 2 && outlet;

  if (maint.blocked) return <MaintenanceScreen />;
  return (
    <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

    <div style={s.page}>
      <CASSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} navigate={navigate} lang={lang} role={user.role} />
      {/* Nav */}
      <nav style={s.nav}>
        <div style={s.navL}>
          <button style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#fff', fontSize: 16, lineHeight: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}
            onClick={() => setSidebarOpen(true)}>
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
          </button>
          <img
            src="/logo-wordmark.svg"
            alt="Ninja Mart"
            width={28 * 3.84}
            height={28}
            style={{ height: 28, width: 28 * 3.84, objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <div style={s.navT}>{t.title}</div>
        </div>
        <div style={s.navR}>
          <div style={{ position: 'relative' }}>
            <div style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              onClick={e => { e.stopPropagation(); setProfileOpen(p => !p); }}>
              <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{user.displayName}<span style={{ display:'inline-block', width:0, height:0, borderLeft:'4px solid transparent', borderRight:'4px solid transparent', borderTop:'5px solid #fff', marginLeft:5, verticalAlign:'middle' }} /></div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }}>{getCASUserTitle(user.email, user.role)} - {regionLabel(user)}</div>
            </div>
            {profileOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 200, overflow: 'hidden' }}>
                <button style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#c5221f', fontWeight: 600 }}
                  onClick={e => { e.stopPropagation(); setProfileOpen(false); logout(); }}>{t.signOut}</button>
              </div>
            )}
          </div>
          <button style={s.langBtn} onClick={toggleLang}>
            <span style={{ opacity: lang === 'bm' ? 1 : 0.5 }}>BM</span>{' | '}
            <span style={{ opacity: lang === 'en' ? 1 : 0.5 }}>EN</span>
          </button>
        </div>
      </nav>

      {/* Sticky outlet card � visible from step 2 onwards */}
      {showStickyCard && (
        <StickyOutletCard
          outlet={outlet} t={t}
          collapsed={outletCardCollapsed}
          setCollapsed={setOutletCardCollapsed}
          lang={lang}
        />
      )}

      {/* Loading popups */}
      {riskLoading && <LoadingPopup message={t.checkingRisk} />}
      {busy && step === 6 && <LoadingPopup message={t.submitting} />}

      <div style={s.body}>
        {err && <div style={s.bannerRed}>{err}</div>}

        {/* -- STEP 0: Outlet Lookup -- */}
        {step === 0 && (
          <div style={s.card}>
            <div style={s.cardTitle}>{t.step1Title}</div>
            <input style={s.input} placeholder={t.outletIdPlaceholder}
              value={outletInput}
              onChange={e => {
              setOutletInput(e.target.value.toUpperCase());
              setOutletNotFound(false); setOutlet(null);
              setRisk(null); setRiskOutletId(null);
              setRegCat(''); setRegSub(''); setRegNo(''); setEditRegNo(false);
              setPortalState(''); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false);
            }}
              onKeyDown={e => e.key === 'Enter' && handleCheck()}
            />
            {outletNotFound && <div style={{ ...s.bannerYellow, marginTop: 10 }}>{t.outletNotFound}</div>}

            {!outlet && (
              <div style={{ ...s.actions, marginTop: 12 }}>
                <button style={s.btnPrimary} onClick={handleCheck} disabled={busy || !outletInput.trim()}>
                  {busy ? <><Spinner size={14} color="#fff" /> &nbsp;{t.checking}</> : t.checkBtn}
                </button>
              </div>
            )}

            {outlet && (
              <>
                <div style={s.divider} />
                {/* Outlet details */}
                <div style={s.grid2}>
                  <KV label={t.outletId}    value={outlet.outletId} />
              <KV label={t.outletName}  value={outlet.outletName} />
                  {(outlet.regionPicked || !String(outlet.van_region || '').trim() || String(outlet.van_region).trim().toLowerCase() === 'unknown') ? (
                    <div>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Region *</div>
                      <select
                        value={outlet.regionPicked ? outlet.van_region : ''}
                        onChange={e => setOutlet(o => ({ ...o, van_region: e.target.value, regionPicked: !!e.target.value }))}
                        style={{ width: '100%', padding: '6px 8px', fontSize: 13 }}
                      >
                        <option value="">Select region...</option>
                        {(regionsOf(user).length ? regionsOf(user) : ['Central', 'North', 'South', 'East Coast 1', 'East Coast 2']).map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <KV label={lang === 'en' ? 'Region' : 'Region'} value={outlet.van_region || '-'} />
                  )}
                  <KV label={t.siteId}      value={outlet.siteId} />
                  <KV label={t.vanId}       value={outlet.vanId} />
                  <KV label={t.creditLimit} value={outlet.creditLimit != null ? fmtRM(outlet.creditLimit) : '-'} />
                  <KV label={t.paymentTerm} value={outlet.paymentTerm ? (lang === 'bm' ? outlet.paymentTerm.replace(/days?/i, 'hari') : outlet.paymentTerm) : '-'} />
                  <KV label={t.regNo}       value={outlet.ssm || '-'} />
                </div>
                <div style={{ ...s.bannerBlue, marginTop: 14 }}>{t.confirmPrompt}</div>
                <div style={s.actions}>
                  <button style={s.btnGrey} onClick={() => { setOutlet(null); setOutletInput(''); }}>{t.confirmNo}</button>
                  <button style={s.btnGreen} onClick={handleRiskCheck}>{t.confirmYes}</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* -- STEP 2: Risk Assessment -- */}
        {step === 2 && risk && (
          <>
            {/* Blacklist card */}
            <div style={tintCard(risk.isBlacklisted ? 'red' : 'green')}>
              <div style={tintTitle(risk.isBlacklisted ? 'red' : 'green')}>{t.blacklistCard}</div>
              {risk.isBlacklisted ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#c8102e', marginBottom: 6 }}>{t.blacklistHit}</div>
                  <div style={{ fontSize: 12, color: '#c8102e', lineHeight: 1.6 }}>
                    {lang === 'en'
                      ? <>Submission is not permitted as this account is blocked. Please contact your credit manager for more information, or <span style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={resetAll}>click here</span> to start a new application for a different outlet.</>
                      : <>Permohonan tidak dibenarkan kerana akaun disekat. Sila hubungi pengurus kredit anda untuk maklumat lanjut atau <span style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={resetAll}>klik di sini</span> untuk membuat permohonan baharu bagi outlet lain.</>
                    }
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 13, color: '#1a1a2e', fontWeight: 500 }}>{t.blacklistNil}</div>
              )}
            </div>

            {/* AR card */}
            <div style={tintCard(risk.isOverdue ? 'red' : 'green')}>
              <div style={tintTitle(risk.isOverdue ? 'red' : 'green')}>{t.arCard}</div>
              <div style={s.grid2}>
                <KV label={t.currentAR}    value={fmtRM(risk.currentAR)} />
                <KV label={t.totalOverdue} value={
                  <span style={{ color: risk.isOverdue ? '#c8102e' : '#1e7e34', fontWeight: 700 }}>
                    {fmtRM(risk.totalOverdue)}
                  </span>
                } />
              </div>
              {risk.isOverdue && risk.overdueInvoices.length > 0 && (
                <>
                  <div style={{ ...s.divider, margin: '10px 0' }} />
                  <table style={s.table}>
                    <thead><tr>
                      <th style={s.th}>{t.invoiceId}</th>
                      <th style={s.th}>{t.invoiceDate}</th>
                      <th style={s.th}>{t.dueDate}</th>
                      <th style={s.th}>{t.amount}</th>
                      <th style={s.th}>{t.dpd}</th>
                    </tr></thead>
                    <tbody>
                      {risk.overdueInvoices.map((inv, i) => (
                        <tr key={i}>
                          <td style={s.td}>{inv.invoiceId}</td>
                          <td style={s.td}>{inv.invoiceDate?.split('T')[0]}</td>
                          <td style={s.td}>{inv.dueDate?.split('T')[0] || '-'}</td>
                          <td style={s.td}>{fmtAmt(inv.amount)}</td>
                          <td style={s.td}><span style={s.badge('#c8102e')}>{inv.dpd}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>



            {/* Previous credit decision */}
            <div style={tintCard('neutral')}>
              <div style={tintTitle('neutral')}>{t.lastLimitCard}</div>
              {!risk.lastLimit ? (
                <div style={{ fontSize: 13, color: '#888' }}>{t.noLastLimit}</div>
              ) : (
                <div style={s.grid2}>
                  <KV label={t.finalLimit}    value={risk.lastLimit.finalLimit ? fmtRM(risk.lastLimit.finalLimit) : '-'} />
                  <KV label={t.effectiveDate} value={risk.lastLimit.effectiveDate} />
                  <KV label={t.decision}      value={risk.lastLimit.decision} />
                  <KV label={t.kycStatus}     value={risk.lastLimit.kycStatus} />
                  <KV label={t.approver}      value={risk.lastLimit.approver} />
                  <KV label={t.source}        value={risk.lastLimit.source} />
                  {risk.lastLimit.creditDecisionRemark && (
                    <div style={{ gridColumn: '1/-1', ...s.kv }}>
                      <div style={s.kvLabel}>{t.remark}</div>
                      <div style={{ ...s.kvValue, fontWeight: 400, fontSize: 12, color: '#555', lineHeight: 1.5 }}>{risk.lastLimit.creditDecisionRemark}</div>
                    </div>
                  )}
                </div>
              )}
            </div>



            {risk.isOverdue && !risk.isBlacklisted && (
              <div style={{ ...s.bannerRed, display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 8 }}>
                <span style={{ fontSize: 16, flexShrink: 0, fontWeight: 700, color: '#c5221f' }}>[X]</span>
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>
                    {lang === 'en' ? 'Submission Blocked' : 'Permohonan Disekat'}
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                    {lang === 'en'
                      ? <>This outlet has an outstanding overdue balance. Please settle the overdue balance first, or <span style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={resetAll}>start a new application</span> for a different outlet.</>
                      : <>Outlet ini mempunyai baki AR tertunggak yang belum dijelas. Sila jelaskan baki tertunggak terlebih dahulu, atau <span style={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={resetAll}>mulakan permohonan baharu</span> bagi outlet yang lain.</>
                    }
                  </div>
                </div>
              </div>
            )}
            <div style={{ fontSize: 11, color: '#888', padding: '4px 0 8px', lineHeight: 1.6 }}>{t.dataDisclaimer}</div>

            <div style={s.actions}>
              <button style={s.btnSecondary} onClick={() => { setStep(0); setRisk(null); setOutletCardCollapsed(false); }}>{t.back}</button>
              <button
                style={{ ...s.btnPrimary, ...(isBlocked ? s.btnDisabled : {}) }}
                disabled={isBlocked}
                onClick={() => !isBlocked && setStep(3)}
              >{t.proceed}</button>
            </div>
          </>
        )}

        {/* -- STEP 3: Registration -- */}
        {step === 3 && (
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>{t.step3Title}</div>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 14, lineHeight: 1.5 }}>{t.step3Instruction}</div>

              <label style={s.label}>{t.regType}</label>
              <select style={s.select} value={regCat} onChange={e => {
                setRegCat(e.target.value); setRegSub('');
                setPortalState(''); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false);
              }}>
                <option value="">{t.silaChoice}</option>
                <option value="SSM">{t.ssmRegistered}</option>
                <option value="NON">{t.nonSsm}</option>
              </select>

              {regCat === 'SSM' && (
                <>
                  <div style={{ marginTop: 12 }} />
                  <label style={s.label}>{t.ssmSubType}</label>
                  <select style={s.select} value={regSub} onChange={e => {
                    setRegSub(e.target.value);
                    setPortalState(''); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false);
                  }}>
                    <option value="">{t.silaChoice}</option>
                    <option value="ROC">{lang === 'en' ? 'ROC - Company (Sdn Bhd / Bhd)' : 'ROC - Syarikat (Sdn Bhd / Bhd)'}</option>
                    <option value="ROB">{lang === 'en' ? 'ROB - Business (Sole Proprietor / Partnership)' : 'ROB - Perniagaan (Milikan Tunggal / Perkongsian)'}</option>
                    <option value="LLP">{lang === 'en' ? 'LLP - Limited Liability Partnership' : 'LLP - Perkongsian Liabiliti Terhad'}</option>
                  </select>
                  {regSub && <div style={s.hint}>{regSub === 'LLP' ? t.llpFormat : t.rocRobFormat}</div>}
                </>
              )}

              {regCat === 'NON' && (
                <>
                  <div style={{ marginTop: 12 }} />
                  <label style={s.label}>{t.nonSsmSubType}</label>
                  <select style={s.select} value={regSub} onChange={e => {
                    setRegSub(e.target.value);
                    setPortalState(''); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false);
                  }}>
                    <option value="">{t.silaChoice}</option>
                    <option value="KOPERASI">Koperasi</option>
                    <option value="SOC">{lang === 'en' ? 'Society / Association' : 'Pertubuhan / Persatuan'}</option>
                    <option value="PPK">{lang === 'en' ? 'PPK (Farmers Organisation)' : 'PPK (Pertubuhan Peladang Kawasan)'}</option>
                    <option value="PERNAMA">PERNAMA / PERWIRA</option>
                    <option value="OTHERS">{lang === 'en' ? 'Others' : 'Lain-lain'}</option>
                  </select>
                  {regSub && regSub !== 'KOPERASI' && (
                    <div style={{ ...s.bannerYellow, marginTop: 8 }}>{t.nonSsmDocNotice}</div>
                  )}
                  {regSub && <div style={s.hint}>{t.nonSsmFormat}</div>}
                </>
              )}

              {regCat && regSub && (
                <>
                  <div style={{ marginTop: 12 }} />
                  <label style={s.label}>{t.regNoLabel}</label>
                  {outlet.ssm && !editRegNo ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ ...s.kvValue, flex: 1, fontSize: 15 }}>{regNo}</div>
                      <button style={{ ...s.btnSecondary, flex: 'none', padding: '6px 14px' }}
                        onClick={() => { setEditRegNo(true); setPortalState(''); setPortalResult(null); }}>
                        {t.regNoEdit}
                      </button>
                    </div>
                  ) : (
                    <input style={s.input} placeholder={t.regNoInput} value={regNo}
                      onChange={e => { setRegNo(e.target.value); setPortalState(''); setPortalResult(null); setPortalBlockMsg(''); setPortalConfirmed(false); }} />
                  )}
                  {regNo.length > 0 && !regNoValid() && <div style={s.err}>{t.regNoRequired}</div>}
                </>
              )}

              {regNoValid() && needsPortal && portalState === '' && (
                <div style={{ marginTop: 16 }}>
                  <button style={{ width: '100%', padding: '12px 0', background: '#c8102e', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 12 }} onClick={handlePortal}>{t.verifyBtn}</button>
                </div>
              )}

              {portalState === 'validating' && (
                <div style={{ ...s.bannerYellow, marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <Spinner size={16} color="#b06000" />
                  <span>{regCat === 'SSM' ? t.ssmValidating : t.skmValidating}</span>
                </div>
              )}

              {portalState === 'success' && portalResult && (
                <div style={{ ...s.bannerGreen, marginTop: 12 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>{t.portalRecord}</div>
                  <div style={s.grid2}>
                    <KV label={t.officialName}   value={portalResult.officialName} />
                    <KV label={t.officialStatus} value={portalResult.officialStatus} />
                  </div>
                </div>
              )}

              {portalState === 'down' && (
                <div style={{ ...s.bannerYellow, marginTop: 12 }}>
                  <div>{t.portalDown}</div>
                  <div style={s.checkbox}>
                    <input type="checkbox" style={s.checkboxI} checked={portalConfirmed}
                      onChange={e => setPortalConfirmed(e.target.checked)} />
                    <label style={s.checkboxL}>{t.portalDownConfirm.replace('{regno}', regNo.trim())}</label>
                  </div>
                </div>
              )}

              {portalState === 'blocked' && (
                <div style={{ ...s.bannerRed, marginTop: 12, whiteSpace: 'pre-line' }}>{portalBlockMsg}</div>
              )}
            </div>

            <div style={s.actions}>
              <button style={s.btnSecondary} onClick={() => setStep(2)}>{t.back}</button>
              <button
                style={{ ...s.btnPrimary, ...((!canProceedStep3 || step3Verifying) ? s.btnDisabled : {}) }}
                disabled={!canProceedStep3 || step3Verifying}
                onClick={handleProceedStep3}
              >{step3Verifying ? <><Spinner size={14} color="#fff" /> &nbsp;{t.verifyingRegistration}</> : t.proceed}</button>
            </div>
          </>
        )}

        {/* -- STEP 4: Credit Request -- */}
        {step === 4 && (
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>{t.step4Title}</div>

              {!risk?.hasRepaymentHistory && (
                <>
                  <div style={s.sectionHead}>{t.newOutletSection}</div>
                  <div style={{ ...s.bannerBlue, fontSize: 12 }}>{t.newOutletInstruction}</div>
                  <label style={s.label}>{t.monthlyPurchase} *</label>
                  <input style={s.input} type="number" min="1" placeholder={t.monthlyPurchasePlaceholder}
                    value={monthlyPurchase} onChange={e => setMonthlyPurchase(e.target.value === '0' ? '' : e.target.value)} />
              {monthlyPurchase !== '' && parseFloat(monthlyPurchase) <= 0 && (
                <div style={s.err}>{lang === 'en' ? 'Please enter a value greater than 0.' : 'Sila masukkan nilai lebih daripada 0.'}</div>
              )}
                  <label style={{ ...s.label, marginTop: 10 }}>{t.skuCount} *</label>
                  <input style={s.input} type="number" min="1" placeholder={t.skuCountPlaceholder}
                    value={skuCount} onChange={e => setSkuCount(e.target.value === '0' ? '' : e.target.value)} />
              {skuCount !== '' && parseInt(skuCount) <= 0 && (
                <div style={s.err}>{lang === 'en' ? 'Please enter a value greater than 0.' : 'Sila masukkan nilai lebih daripada 0.'}</div>
              )}
                  <label style={{ ...s.label, marginTop: 10 }}>{t.brands} *</label>
                  <input style={s.input} placeholder={t.brandsPlaceholder}
                    value={brands} onChange={e => setBrands(e.target.value)} />
                  <div style={s.divider} />
                </>
              )}

              <label style={s.label}>{t.reqLimit} *</label>
              <input style={s.input} type="number" min="1" placeholder={t.reqLimitPlaceholder}
  value={reqLimit}
  onChange={e => setReqLimit(e.target.value === '0' ? '' : e.target.value)} />
              {reqLimit !== '' && parseFloat(reqLimit) <= 0 && (
  <div style={s.err}>{lang === 'en' ? 'Please enter a valid amount greater than 0.' : 'Sila masukkan jumlah sah yang lebih daripada 0.'}</div>
)}
{reqLimit && parseFloat(reqLimit) > 0 && outlet?.creditLimit > 0 && parseFloat(reqLimit) < parseFloat(outlet.creditLimit) && (
  <div style={s.err}>{t.reqLimitBelowCurrent.replace('{limit}', fmtAmt(outlet.creditLimit))}</div>
)}

              {needsJustification() && (
                <>
                  <div style={s.divider} />
                  <label style={s.label}>{t.justificationLabel} *</label>
                  <div style={s.hint}>{t.justificationHint}</div>
                  <textarea style={s.textarea} placeholder={t.justificationPlaceholder}
                    value={justification} onChange={e => setJustification(e.target.value)} />
                </>
              )}

              {risk?.hasLatePayments && (
                <>
                  <div style={s.divider} />
                  <div style={s.sectionHead}>{t.latePaymentSection}</div>
                  <div style={s.hint}>{t.latePaymentHint}</div>
                  <table style={{ ...s.table, marginBottom: 12 }}>
                    <thead><tr>
                      <th style={s.th}>{t.invoiceId}</th>
                      <th style={s.th}>{t.invoiceDate}</th>
                      <th style={s.th}>{t.dueDate}</th>
                      <th style={s.th}>{t.amount}</th>
                      <th style={s.th}>{t.dpd}</th>
                    </tr></thead>
                    <tbody>
                      {risk.latePaymentHistory.map((inv, i) => (
                        <tr key={i}>
                          <td style={s.td}>{inv.invoiceId}</td>
                          <td style={s.td}>{inv.invoiceDate?.split('T')[0]}</td>
                          <td style={s.td}>{inv.dueDate?.split('T')[0] || '-'}</td>
                          <td style={s.td}>{fmtAmt(inv.amount)}</td>
                          <td style={s.td}><span style={s.badge('#b06000')}>{inv.dpd}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <label style={s.label}>{t.latePaymentResponseLabel} *</label>
                  <textarea style={s.textarea}
  value={latePayResp} onChange={e => setLatePayResp(e.target.value)} />
                </>
              )}

              <div style={s.divider} />
              <label style={s.label}>{t.otherRemarks}</label>
              <div style={s.hint}>{t.otherRemarksHint}</div>
              <textarea style={s.textarea} placeholder={t.otherRemarksPlaceholder}
                value={otherRemarks} onChange={e => setOtherRemarks(e.target.value)} />
            </div>

            {(() => {
              const req = parseFloat(reqLimit) || 0;
              const curr = parseFloat(outlet?.creditLimit) || 0;
              const disabled = (
                !reqLimit || req <= 0 ||
                (curr && req < curr) ||
                (needsJustification() && !justification.trim()) ||
                (risk?.hasLatePayments && !latePayResp.trim()) ||
                (!risk?.hasRepaymentHistory && (!monthlyPurchase || parseFloat(monthlyPurchase) <= 0 || !skuCount || parseInt(skuCount) <= 0 || !brands.trim()))
              );
              return (
                <div style={s.actions}>
                  <button style={s.btnSecondary} onClick={() => setStep(3)}>{t.back}</button>
                  <button style={{ ...s.btnPrimary, ...(disabled ? s.btnDisabled : {}) }}
                    disabled={disabled} onClick={() => setStep(5)}>{t.proceed}</button>
                </div>
              );
            })()}
          </>
        )}

        {/* -- STEP 5: Documents -- */}
        {step === 5 && (
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>{t.step5Title}</div>
              <FileField label={t.bizRegDoc} hint={t.bizRegDocHint} files={bizRegFiles} setter={setBizRegFiles} maxFiles={1} id="bizReg" />
              <div style={s.divider} />
              <FileField label={t.pdcDocs} hint={t.pdcDocsHint} files={pdcFiles} setter={setPdcFiles} maxFiles={5} id="pdc" />
              <div style={s.divider} />
              <FileField label={t.financialDoc} hint={t.financialDocHint} files={financialFiles} setter={setFinancialFiles} maxFiles={3} id="financial" />
              <div style={s.divider} />
              <FileField label={t.ctosConsent} hint={t.ctosConsentHint} files={ctosFiles} setter={setCtosFiles} maxFiles={1} id="ctos">
                <div style={{ marginBottom: 8 }}>
                  <a href="https://drive.google.com/uc?export=download&id=1ujEN7_sdsK_dsjZ-g4HR_dMrBhcG6mma"
                    target="_blank" rel="noreferrer" style={{ marginRight: 12, fontSize: 12, color: '#1967d2' }}>{t.downloadEN}</a>
                  <a href="https://drive.google.com/uc?export=download&id=1DhiUQ7mJ9JcpmoWhdPEXYhd_YFAJz5z1"
                    target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#1967d2' }}>{t.downloadBM}</a>
                </div>
              </FileField>
            </div>
            <div style={s.actions}>
              <button style={s.btnSecondary} onClick={() => setStep(4)}>{t.back}</button>
              <button style={s.btnPrimary} onClick={() => setStep(6)}>{t.proceed}</button>
            </div>
          </>
        )}

        {/* -- STEP 6: Summary -- */}
        {step === 6 && (
          <>
            <div style={s.card}>
              <div style={s.cardTitle}>{t.step6Title}</div>

              <div style={s.sectionHead}>{t.registration}</div>
              <div style={s.grid2}>
                <KV label={t.regType}    value={regCat === 'SSM' ? t.ssmRegistered : t.nonSsm} />
                <KV label={t.regNoLabel} value={regNo} />
                {portalResult && <KV label={t.officialName}   value={portalResult.officialName} />}
                {portalResult && <KV label={t.officialStatus} value={portalResult.officialStatus} />}
              </div>

              <div style={s.divider} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={s.sectionHead}>{t.creditRequest}</div>
                <button style={{ ...s.btnSecondary, flex: 'none', padding: '4px 12px', fontSize: 12 }}
                  onClick={() => setStep(4)}>{t.edit}</button>
              </div>
              <div style={s.grid2}>
                <KV label={t.reqLimit} value={fmtRM(parseFloat(reqLimit))} />
                {!risk?.hasRepaymentHistory && (
                  <>
                    <KV label={t.monthlyPurchase} value={fmtRM(parseFloat(monthlyPurchase))} />
                    <KV label={t.skuCount}        value={skuCount} />
                    <KV label={t.brands}          value={brands} />
                  </>
                )}
              </div>
              {justification    && <KV label={t.justificationLabel}      value={justification} />}
              {latePayResp      && <KV label={t.latePaymentResponseLabel} value={latePayResp} />}
              {otherRemarks     && <KV label={t.otherRemarks}            value={otherRemarks} />}

              <div style={s.divider} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={s.sectionHead}>{t.documents}</div>
                <button style={{ ...s.btnSecondary, flex: 'none', padding: '4px 12px', fontSize: 12 }}
                  onClick={() => setStep(5)}>{t.edit}</button>
              </div>
              {[
                { label: t.bizRegDoc,    files: bizRegFiles },
                { label: t.pdcDocs,      files: pdcFiles },
                { label: t.financialDoc, files: financialFiles },
                { label: t.ctosConsent,  files: ctosFiles },
              ].map(({ label, files }) => (
                <KV key={label} label={label}
                  value={files.length > 0 ? t.filesUploaded.replace('{n}', files.length) : t.notUploaded} />
              ))}
            </div>

            <div style={s.actions}>
              <button style={s.btnSecondary} onClick={() => setStep(5)}>{t.back}</button>
              <button style={s.btnPrimary} onClick={handleSubmit} disabled={busy}>
                {busy ? <><Spinner size={14} color="#fff" /> &nbsp;{t.submitting}</> : t.submit}
              </button>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Cross-region popup */}
    {crossRegionPopup && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px',
      }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 20px', maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>
            {t.crossRegionTitle}
          </div>
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
            {t.crossRegionMsg(outlet?.van_region || '')}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={s.btnGrey} onClick={() => { setCrossRegionPopup(false); setPendingRiskAfterRegion(false); }}>
              {t.crossRegionBack}
            </button>
            <button style={s.btnPrimary} onClick={() => {
              setCrossRegionPopup(false);
              setPendingRiskAfterRegion(false);
              handleRiskCheck(true);
            }}>
              {t.crossRegionProceed}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Resubmission popup */}
    {resubmissionPopup && (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px',
      }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 20px', maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>
            {resubmissionPopup.type === 'pending' ? t.resubPendingTitle
              : resubmissionPopup.type === 'declined_within_30' ? t.resubDeclinedTitle
              : resubmissionPopup.type === 'appeal_exhausted' ? t.resubAppealExhaustedTitle
              : t.resubApprovedTitle}
          </div>
          <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>
            {resubmissionPopup.type === 'pending'
              ? t.resubPendingMsg(resubmissionPopup.mcRow, resubmissionPopup.submittedAt?.split('T')[0] || '')
              : resubmissionPopup.type === 'declined_within_30'
              ? t.resubDeclinedMsg(resubmissionPopup.mcRow, resubmissionPopup.approvalDt || '')
              : resubmissionPopup.type === 'appeal_exhausted'
              ? t.resubAppealExhaustedMsg(resubmissionPopup.mcRow, resubmissionPopup.approvalDt || '', resubmissionPopup.reopensAt || '')
              : t.resubApprovedMsg(resubmissionPopup.mcRow, resubmissionPopup.approvalDt || '')}
          </div>
          {resubmissionPopup.type === 'declined_within_30' && (
            <>
              <label style={s.label}>{t.resubJustLabel} *</label>
              <textarea style={{ ...s.textarea, minHeight: 100 }} placeholder={t.resubJustPlaceholder}
                value={resubmitJustification} onChange={e => setResubmitJustification(e.target.value)} />
              <div style={{ fontSize: 11, color: resubmitJustification.length >= 100 ? '#1e7e34' : '#888', marginBottom: 12 }}>
                {t.resubCharCount(resubmitJustification.length)}
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            {(resubmissionPopup.type === 'pending' || resubmissionPopup.type === 'appeal_exhausted') ? (
              <button style={{ ...s.btnPrimary, flex: 1 }} onClick={handleResubmitBack}>{t.resubBack}</button>
            ) : (
              <>
                <button style={s.btnGrey} onClick={handleResubmitBack}>{t.resubBack}</button>
                <button
                  style={{ ...s.btnPrimary, ...(resubmissionPopup.type === 'declined_within_30' && resubmitJustification.length < 100 ? s.btnDisabled : {}) }}
                  disabled={resubmissionPopup.type === 'declined_within_30' && resubmitJustification.length < 100}
                  onClick={handleResubmitProceed}
                >{t.resubProceed}</button>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}